// cutscene-ui.ts — the in-mission CUTSCENE player overlay (domCutScene, kidfCutScene 1047), the
// "opening screens" a CutSceneAction trigger performs: a TOP image (an <img NAME> atlas .rbm/.png)
// + typed narration that REFLOWS below it, paged with "More…". The SAME story-modal family as the
// E-Com (reuses the typewriter reveal + the 9-slice button), but the full-screen cutscene plays
// over BLACK — so the HOST hides the game canvas while it's up (intro-over-black), driven by the
// onActiveChange callback. The module OWNS the `active` boolean; isActive() feeds the host's
// domStoryOverlayActive guard + the HUD-suppressed gate. On dismiss it unmounts + calls done()
// (releases the story-modal pause + shows the next queued modal). Lifted from main.ts's mountCutScene
// (a sibling of command-ui.ts / hud-seams.ts / objectives-ui.ts / ecom-ui.ts), so the mission
// editor's Play host and the shipped client share ONE implementation.
//
// The host (not the module) hides/shows the canvas: the module knows nothing of #screen or
// syncReveal — it only flips `active` and notifies via onActiveChange so each host applies its own
// reveal (main.ts keeps its inline cutscene this round; the editor playhost hides its <canvas>).

import { CutSceneView, type CutSceneImage } from './cutscene-view.ts';
import type { CutSceneInfo } from '../../ui/cutscene.ts';
import {
  extractAtlasArt,
  titleArtToDataUrl,
  extractButtonArt,
  type ButtonArt,
} from '../title-art.ts';
import type { TexAtlasMgr } from '../../gfx/texatlasmgr.ts';

export interface CutSceneUiDeps {
  /** the texture atlas mgr — sources the cutscene <img NAME> bitmaps + the 9-slice button frame. */
  tam: TexAtlasMgr;
  /** optional pre-extracted 9-slice button art; else extracted once via extractButtonArt(tam). */
  objBtnArt?: ButtonArt | null;
  /** play a GUI sfx on a button press, wired to the host SoundMgr (no-op headless). */
  playSfx: (sfx: number) => void;
  /** the element the overlay root mounts into (default document.body). The root is
   *  `position:fixed; inset:0`; a TRANSFORMED pane-covering host element bounds it to the
   *  pane instead of the window (the mission editor's play-in-place WYSIWYG mode). */
  host?: HTMLElement;
  /** the cutscene plays over BLACK: the host hides the game canvas while it's active (intro-over-
   *  black) and reveals it on close. Called with true BEFORE the view builds (so the canvas hides
   *  before paint) and false on unmount. Absent ⇒ no canvas hide (a headless harness). */
  onActiveChange?: (active: boolean) => void;
}

export interface CutSceneUi {
  /** the domCutScene session opt (gameSessionOpts.domCutScene) — CutScene() calls this with the
   *  resolved briefing; `done()` releases the story-modal pause + advances the queue. */
  domCutScene: (info: CutSceneInfo, done: () => void) => void;
  /** is the full-screen cutscene still mounted? Feeds the host's domStoryOverlayActive guard
   *  (WireJumpToMission waits on it) + the HUD-suppressed gate (no HUD/labels/radar behind it). */
  isActive: () => boolean;
  /** idempotently unmount the cutscene if it's up (game-over / host close) — also reveals the
   *  canvas via onActiveChange(false). */
  teardown(): void;
}

/** Stand up the shared in-mission cutscene overlay for one play session. */
export function createCutSceneUI(deps: CutSceneUiDeps): CutSceneUi {
  const { tam, playSfx, onActiveChange } = deps;
  const objBtnArt: ButtonArt | undefined = deps.objBtnArt ?? extractButtonArt(tam) ?? undefined;

  // the cutscene <img> names are .rbm authored as .png atlas entries; extractAtlasArt gives the
  // natural GAME-px size the view scales by gs (the bitmap-sizing lesson). Cached per name.
  const imgCache = new Map<string, CutSceneImage | null>();
  const loadImage = (name: string): CutSceneImage | null => {
    if (imgCache.has(name)) return imgCache.get(name)!;
    const art = extractAtlasArt(tam, name);
    const img: CutSceneImage | null =
      art === null ? null : { url: titleArtToDataUrl(art), cx: art.cx, cy: art.cy };
    imgCache.set(name, img);
    return img;
  };

  const root = document.createElement('div');
  root.id = 'dom-cutscene';
  root.style.display = 'none';
  (deps.host ?? document.body).appendChild(root);
  let view: CutSceneView | null = null;
  let active = false; // the module OWNS this; isActive() feeds the host's story-overlay + HUD gates

  const unmount = (): void => {
    root.style.display = 'none';
    view?.destroy();
    view = null;
    root.replaceChildren();
    // the intro is over — flip the flag, then let the host reveal the canvas + (re)mount the HUD
    // (the host's onActiveChange, NOT a syncReveal here — that's the host's job).
    active = false;
    onActiveChange?.(false);
  };

  const domCutScene = (info: CutSceneInfo, done: () => void): void => {
    unmount(); // destroy any prior view first (no leaked listeners/rAF) — matches mountBuildPanel
    root.style.display = 'flex';
    // the intro plays over BLACK: set active FIRST + notify the host so it hides the game canvas
    // BEFORE the view builds (so #screen hides before paint).
    active = true;
    onActiveChange?.(true);
    view = new CutSceneView({
      root,
      message: info.message,
      loadImage,
      buttonArt: objBtnArt,
      playSfx: (sfx: number) => {
        playSfx(sfx);
      },
      // dismiss: unmount FIRST, then run done() (release the story-modal pause + show the next
      // queued modal) so a follow-on modal isn't behind the cutscene.
      onClose: () => {
        unmount();
        done();
      },
    });
  };

  return { domCutScene, isActive: () => active, teardown: unmount };
}
